import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Search, Eye, ChevronDown, Check } from "lucide-react";
import api, { formatDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import CountryPhoneInput from "@/components/CountryPhoneInput";
import { CylinderTypeSelect, useCylinderTypes } from "@/components/CylinderTypeSelect";
import { StationFields, geoPayload } from "@/components/household/StationFields";
import { useStations } from "@/components/supply/shared";

const emptyForm = {
  household_name: "", phone: "", email: "", address: "", meter_id: "", cylinder_type_id: "", station_id: "", lat: "", lng: "",
};

// Searchable dropdown of available (unassigned) meters
function MeterPicker({ value, onChange, meters }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const selected = meters.find((m) => m.id === value);
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return meters;
    return meters.filter((m) =>
      (m.serial_number || "").toLowerCase().includes(q) ||
      (m.imei || "").toLowerCase().includes(q) ||
      (m.deveui || "").toLowerCase().includes(q) ||
      (m.sim_number || "").toLowerCase().includes(q),
    );
  }, [query, meters]);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid="household-meter-trigger"
          className="w-full h-10 flex items-center justify-between gap-2 border border-slate-300 rounded-sm bg-white px-3 text-sm text-left hover:border-brand-400 focus:outline-none focus:ring-2 focus:ring-brand-500/30 focus:border-brand-500"
        >
          <span className={selected ? "text-slate-800" : "text-slate-400"}>
            {selected
              ? <>{selected.serial_number}<span className="text-slate-400 font-mono ml-2">· {(selected.imei || selected.deveui || "").slice(-6)}</span></>
              : "Choose a meter (optional)"}
          </span>
          <ChevronDown size={14} className="text-slate-400" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-[360px] p-0 max-h-[380px] overflow-hidden flex flex-col">
        <div className="p-2 border-b border-slate-100">
          <div className="relative">
            <Search size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              autoFocus
              data-testid="household-meter-search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search by meter number, IMEI, SIM…"
              className="w-full pl-7 pr-2 py-1.5 text-sm rounded-sm border border-slate-200 focus:outline-none focus:ring-1 focus:ring-brand-500"
            />
          </div>
        </div>
        <div className="overflow-y-auto flex-1">
          <button
            type="button"
            onClick={() => { onChange(""); setOpen(false); }}
            className="w-full flex items-center gap-3 px-3 py-2 text-sm text-slate-500 hover:bg-slate-100 text-left border-b border-slate-100"
          >
            <span className="italic">— None (assign later) —</span>
          </button>
          {filtered.length === 0 ? (
            <div className="text-center text-sm text-slate-400 py-6">No matches</div>
          ) : filtered.map((m) => (
            <button
              type="button"
              key={m.id}
              onClick={() => { onChange(m.id); setOpen(false); setQuery(""); }}
              data-testid={`household-meter-option-${m.id}`}
              className="w-full flex items-center gap-3 px-3 py-2 text-sm hover:bg-slate-100 text-left"
            >
              <div className="flex-1 min-w-0">
                <div className="font-medium text-slate-800 truncate">{m.serial_number}</div>
                <div className="text-[11px] text-slate-500 font-mono truncate">
                  {m.imei ? `IMEI ${m.imei}` : m.deveui ? `DevEUI ${m.deveui}` : "—"}
                  {m.sim_number ? ` · SIM +${m.sim_number}` : ""}
                </div>
              </div>
              {m.id === value && <Check size={14} className="text-brand-600" />}
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}

export default function Households() {
  const nav = useNavigate();
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [areaFilter, setAreaFilter] = useState("all");
  const [areas, setAreas] = useState([]);
  const [availableMeters, setAvailableMeters] = useState([]);
  const [meters, setMeters] = useState([]);            // all meters (for meter number lookup)
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const cylTypes = useCylinderTypes();
  const [stations] = useStations();
  const typeName = (id) => cylTypes.find((t) => t.id === id)?.name || "—";

  const load = async () => {
    const params = { page, page_size: 20 };
    if (search) params.search = search;
    if (areaFilter && areaFilter !== "all") params.area_id = areaFilter;
    const { data } = await api.get("/households", { params });
    setRows(data.data || []); setTotal(data.total || 0);
  };
  const loadRefs = async () => {
    const [a, m] = await Promise.all([
      api.get("/areas"),
      api.get("/meters", { params: { page_size: 500 } }),
    ]);
    setAreas(a.data.data || []);
    setMeters(m.data.data || []);
    // Unassigned only, for the picker
    setAvailableMeters((m.data.data || []).filter((x) => !x.household_id));
  };
  useEffect(() => { loadRefs(); }, []);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [page, search, areaFilter]);

  const openCreate = () => {
    setForm({ ...emptyForm, cylinder_type_id: (cylTypes.find((t) => t.is_default) || cylTypes[0])?.id || "" });
    setEditingId(null); setErr(""); setOpen(true);
    loadRefs();
  };
  const openEdit = (r) => {
    setForm({
      household_name: r.household_name || "",
      phone: r.phone || "",
      email: r.email || "",
      address: r.address || "",
      meter_id: "",
      cylinder_type_id: r.cylinder_type_id || "",
      station_id: r.station_id || "", lat: r.lat ?? "", lng: r.lng ?? "",
    });
    setEditingId(r.id); setErr(""); setOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!form.household_name.trim()) { setErr("Full name is required"); return; }
    if (!form.cylinder_type_id) { setErr("Cylinder type is required"); return; }
    setSaving(true);
    try {
      const payload = {
        household_name: form.household_name.trim(),
        phone: form.phone || "",
        email: (form.email || "").trim(),
        address: (form.address || "").trim(),
        meter_id: form.meter_id || null,
        cylinder_type_id: form.cylinder_type_id,
        ...geoPayload(form),
      };
      if (editingId) {
        const { meter_id, ...rest } = payload;
        await api.put(`/households/${editingId}`, rest);
      } else {
        await api.post("/households", payload);
      }
      toast.success(editingId ? "Household updated" : "Household created");
      setOpen(false); load();
    } catch (err) {
      setErr(formatDetail(err.response?.data?.detail) || err.message);
    } finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this household?")) return;
    try { await api.delete(`/households/${id}`); toast.success("Deleted"); load(); }
    catch (err) { toast.error(formatDetail(err.response?.data?.detail)); }
  };

  const areaName = (id) => areas.find((a) => a.id === id)?.name || "—";

  return (
    <div className="space-y-6" data-testid="households-page">
      <div className="flex items-start justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-slate-500 font-semibold">CRM</div>
          <h1 className="font-heading text-3xl font-bold text-slate-900 tracking-tight mt-1">Households</h1>
          <p className="text-sm text-slate-500 mt-1">Customer accounts assigned to meters.</p>
        </div>
        <Button onClick={openCreate} data-testid="household-add-button"
          className="bg-brand-600 hover:bg-brand-700 rounded-sm">
          <Plus size={16} className="mr-1" /> Create household
        </Button>
      </div>

      <div className="bg-white border border-slate-200 rounded-sm">
        <div className="p-4 border-b border-slate-200 flex flex-col md:flex-row md:items-center gap-3">
          <div className="relative flex-1 max-w-sm">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input placeholder="Search name, phone, number, address…"
              className="pl-9 rounded-sm"
              value={search} onChange={(e) => { setPage(1); setSearch(e.target.value); }}
              data-testid="household-search-input" />
          </div>
          <Select value={areaFilter} onValueChange={(v) => { setPage(1); setAreaFilter(v); }}>
            <SelectTrigger className="w-52 rounded-sm" data-testid="household-area-filter">
              <SelectValue placeholder="All areas" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All areas</SelectItem>
              {areas.map((a) => (<SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>))}
            </SelectContent>
          </Select>
          <div className="ml-auto text-xs text-slate-500 tabular-nums">{total} total</div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 text-[11px] uppercase tracking-wider">
              <tr>
                <th className="text-left px-4 py-2 font-semibold">Household name</th>
                <th className="text-left px-4 py-2 font-semibold">Phone</th>
                <th className="text-left px-4 py-2 font-semibold">Meter number</th>
                <th className="text-left px-4 py-2 font-semibold">Cylinder</th>
                <th className="text-left px-4 py-2 font-semibold">Status</th>
                <th className="text-right px-4 py-2 font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody data-testid="households-table-body">
              {rows.length === 0 && (
                <tr><td colSpan={6} className="p-8 text-center text-sm text-slate-400">No households.</td></tr>
              )}
              {rows.map((r) => {
                const meter = meters.find((m) => m.household_id === r.id);
                const isActive = Boolean(meter);
                return (
                  <tr key={r.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`household-row-${r.id}`}>
                    <td className="px-4 py-2">
                      <div className="font-medium text-slate-800">{r.household_name}</div>
                    </td>
                    <td className="px-4 py-2 font-mono tabular-nums text-[13px] text-slate-700">
                      {r.phone ? `+${r.phone}` : <span className="text-slate-400">—</span>}
                    </td>
                    <td className="px-4 py-2 font-mono text-[13px] text-slate-700">
                      {meter ? meter.serial_number : <span className="text-slate-400">— unassigned —</span>}
                    </td>
                    <td className="px-4 py-2 text-[13px] text-slate-700" data-testid={`household-cyl-type-${r.id}`}>{typeName(r.cylinder_type_id)}</td>
                    <td className="px-4 py-2">
                      <span
                        data-testid={`household-status-${r.id}`}
                        className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-sm text-[11px] font-semibold border ${
                          isActive
                            ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                            : "bg-slate-100 text-slate-600 border-slate-200"
                        }`}
                      >
                        <span className={`h-1.5 w-1.5 rounded-full ${isActive ? "bg-emerald-500" : "bg-slate-400"}`} />
                        {isActive ? "Active" : "Dormant"}
                      </span>
                    </td>
                    <td className="px-4 py-2 text-right">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => nav(`/households/${r.id}`)}
                        data-testid={`household-view-${r.id}`}
                        className="rounded-sm h-7 px-2"
                      >
                        <Eye size={13} className="mr-1" /> View
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {total > 20 && (
          <div className="flex items-center justify-end gap-2 p-3 border-t border-slate-200 text-sm">
            <Button variant="outline" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}
              className="rounded-sm">Prev</Button>
            <div className="tabular-nums text-slate-600">Page {page} / {Math.ceil(total / 20)}</div>
            <Button variant="outline" disabled={page >= Math.ceil(total / 20)} onClick={() => setPage((p) => p + 1)}
              className="rounded-sm">Next</Button>
          </div>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md rounded-sm" data-testid="household-form-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">{editingId ? "Edit household" : "Create household"}</DialogTitle>
            <DialogDescription>{editingId ? "Update the household's contact details." : "Register a new customer account and optionally assign a meter."}</DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-600 font-semibold">Full name</Label>
              <Input
                required
                value={form.household_name}
                onChange={(e) => setForm({ ...form, household_name: e.target.value })}
                data-testid="household-name-input"
                className="mt-1 rounded-sm"
                placeholder="e.g. Amina Mwangi"
                autoFocus
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-600 font-semibold">Phone number</Label>
              <div className="mt-1">
                <CountryPhoneInput
                  value={form.phone}
                  onChange={(v) => setForm({ ...form, phone: v })}
                  testId="household-phone"
                />
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-600 font-semibold">Email <span className="text-slate-400 normal-case">(optional)</span></Label>
              <Input
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                data-testid="household-email-input"
                className="mt-1 rounded-sm"
                placeholder="customer@example.com"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-600 font-semibold">Address</Label>
              <Input
                value={form.address}
                onChange={(e) => setForm({ ...form, address: e.target.value })}
                data-testid="household-address-input"
                className="mt-1 rounded-sm"
                placeholder="Street, area, city"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-600 font-semibold">Cylinder type</Label>
              <CylinderTypeSelect types={cylTypes} value={form.cylinder_type_id} onChange={(v) => setForm({ ...form, cylinder_type_id: v })} testId="household-cylinder-type" />
            </div>
            <StationFields form={form} setForm={setForm} stations={stations} testPrefix="household" />
            {!editingId && (
              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-600 font-semibold">Assign meter <span className="text-slate-400 normal-case">(optional)</span></Label>
                <div className="mt-1">
                  <MeterPicker
                    value={form.meter_id}
                    onChange={(v) => setForm({ ...form, meter_id: v })}
                    meters={availableMeters}
                  />
                </div>
                <div className="text-[10px] text-slate-400 mt-1">Only unassigned meters are listed. You can assign one later from the Meters page.</div>
              </div>
            )}
            {err && (
              <div className="border border-red-200 bg-red-50 text-red-700 text-xs px-3 py-2 rounded-sm" data-testid="household-form-error">
                {err}
              </div>
            )}
            <DialogFooter className="pt-2">
              <Button type="button" variant="outline" onClick={() => setOpen(false)} className="rounded-sm">Cancel</Button>
              <Button type="submit" disabled={saving} data-testid="household-form-submit"
                className="bg-brand-600 hover:bg-brand-700 rounded-sm">
                {saving ? "Saving…" : editingId ? "Save changes" : "Create household"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
